import { useEffect, useRef, useState } from 'react'
import { Link } from 'wouter'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ds'
import { toast } from 'sonner'
import { api, ApiError } from '@/lib/api'
import { cleanRut, formatRut, validateRut } from '@/lib/rut'
import { coincideConMascara } from '@/lib/email-mask'
import { cn } from '@/lib/utils'
import {
  AccesoCuerpo,
  AccesoLayout,
  AccesoPieTarjeta,
  AccesoTitulo,
  CampoAcceso,
  PasosAcceso,
  claseBotonAcceso,
} from '@/components/auth'

type Paso = 'rut' | 'email' | 'enviado'

interface OpcionReset {
  email_mask: string
}

interface LookupResponse {
  opciones: OpcionReset[]
}

const PASOS = ['Tu RUT', 'Tu correo', 'Enlace enviado']

const CONSEJOS = [
  { icono: 'schedule', texto: 'El enlace sirve una sola vez y expira en 7 días.' },
  {
    icono: 'folder_open',
    texto: 'Si no aparece en unos minutos, revisa spam o correo no deseado.',
  },
  {
    icono: 'shield',
    texto: 'Nadie de Fácil Control te va a pedir tu contraseña por teléfono ni por correo.',
  },
]

const mailValido = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim())

export function RecuperarContrasena() {
  const [paso, setPaso] = useState<Paso>('rut')
  const [rut, setRut] = useState('')
  const [rutTocado, setRutTocado] = useState(false)
  const [email, setEmail] = useState('')
  const [emailTocado, setEmailTocado] = useState(false)
  const [opciones, setOpciones] = useState<OpcionReset[]>([])
  const [mascaraElegida, setMascaraElegida] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const emailRef = useRef<HTMLInputElement>(null)

  // Seleccionar una pista habilita el input de correo completo y lo enfoca.
  // La máscara es lossy (no se puede reconstruir el email): la selección es
  // solo una affordance de reconocimiento, nunca un prefill.
  useEffect(() => {
    if (mascaraElegida !== null) emailRef.current?.focus()
  }, [mascaraElegida])

  const rutValido = validateRut(rut)
  const rutError = rutTocado && rut.length > 0 && !rutValido ? 'Ese RUT no es válido' : ''

  // Con pistas en pantalla podemos verificar la coincidencia EN EL CLIENTE:
  // recalculamos la máscara sobre lo que la persona escribió y la comparamos
  // con la que eligió. No revela nada (el texto lo tipeó ella) y evita el
  // viaje al servidor para descubrir que se equivocó de correo.
  const desajustado =
    mascaraElegida !== null && email.trim() !== '' && !coincideConMascara(email, mascaraElegida)

  // Sin pistas (RUT desconocido) no hay contra qué comparar: sólo pedimos que
  // el correo tenga forma de correo y dejamos que el backend decida.
  const emailOk =
    mascaraElegida !== null ? !desajustado && mailValido(email) : mailValido(email)

  const emailBloqueado = opciones.length > 0 && mascaraElegida === null

  const handleRut = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading) return
    if (!rutValido) {
      // El botón se ve inactivo pero recibe el click: es acá donde marcamos el
      // campo, en vez de dejar a la persona apretando un botón mudo.
      setRutTocado(true)
      return
    }

    setIsLoading(true)
    try {
      const res = await api.post<LookupResponse>('/auth/forgot-password/lookup', { rut })
      // El backend devuelve siempre 200 con `opciones[]`. Si el RUT no existe,
      // viene vacío — igual avanzamos al paso 2 para no enumerar usuarios.
      setOpciones(res.opciones)
      // Con una sola pista no hay nada que elegir: la damos por elegida y le
      // ahorramos el click. Con dos o más, elegir es parte de la verificación.
      setMascaraElegida(res.opciones.length === 1 ? res.opciones[0].email_mask : null)
      setPaso('email')
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'No se pudo conectar con el servidor.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading) return
    if (!emailOk) {
      setEmailTocado(true)
      return
    }

    setIsLoading(true)
    try {
      await api.post('/auth/forgot-password', { rut, email })
      setPaso('enviado')
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'No se pudo conectar con el servidor.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  /** Volver al paso 1 y limpiar la elección: el RUT nuevo trae otras pistas. */
  const cambiarRut = () => {
    setPaso('rut')
    setOpciones([])
    setMascaraElegida(null)
    setEmail('')
    setEmailTocado(false)
  }

  const numeroPaso = paso === 'rut' ? 1 : paso === 'email' ? 2 : 3

  return (
    <AccesoLayout>
      <PasosAcceso pasos={PASOS} actual={numeroPaso} />

      {paso === 'rut' && (
        <form onSubmit={handleRut} noValidate>
          <AccesoCuerpo className="pt-5">
            <AccesoTitulo
              titulo="Recuperar contraseña"
              detalle="Ingresa tu RUT. Te mostramos pistas de los correos asociados a tu cuenta para que confirmes cuál es el tuyo."
            />
            <CampoAcceso
              id="rec-rut"
              label="RUT"
              icono="badge"
              mono
              placeholder="12.345.678-5"
              autoComplete="username"
              autoCapitalize="characters"
              value={formatRut(rut)}
              onChange={(v) => setRut(cleanRut(v))}
              onBlur={() => setRutTocado(true)}
              error={rutError}
              disabled={isLoading}
            />
            <Button
              type="submit"
              size="lg"
              className={cn('w-full', claseBotonAcceso(rutValido))}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Icon name="progress_activity" size={18} className="animate-spin" />
                  Buscando…
                </>
              ) : (
                <>
                  Continuar
                  <Icon name="arrow_forward" size={18} />
                </>
              )}
            </Button>
          </AccesoCuerpo>
        </form>
      )}

      {paso === 'email' && (
        <form onSubmit={handleEmail} noValidate>
          <AccesoCuerpo className="pt-5">
            <AccesoTitulo
              titulo="Confirma tu correo"
              detalle={
                opciones.length > 0
                  ? `Encontramos ${
                      opciones.length === 1
                        ? 'un correo asociado'
                        : `${opciones.length} correos asociados`
                    } para ese RUT. Elige el tuyo y escríbelo completo: así confirmamos que la cuenta es tuya.`
                  : 'Si ese RUT está registrado, te enviamos el enlace al correo asociado. Escríbelo completo para confirmarlo.'
              }
            />

            {opciones.length > 0 && (
              <fieldset className="flex flex-col gap-2">
                <legend className="sr-only">Correos asociados a tu cuenta</legend>
                {opciones.map((opcion, i) => {
                  const activa = mascaraElegida === opcion.email_mask
                  return (
                    <label
                      key={`${opcion.email_mask}-${i}`}
                      className={cn(
                        'flex cursor-pointer items-center gap-[11px] rounded-[9px] border px-[13px] py-[11px] transition-colors',
                        activa
                          ? 'border-primary bg-primary-50'
                          : 'border-input hover:border-border-strong'
                      )}
                    >
                      <input
                        type="radio"
                        name="pista-correo"
                        className="sr-only"
                        checked={activa}
                        onChange={() => {
                          setMascaraElegida(opcion.email_mask)
                          setEmail('')
                          setEmailTocado(false)
                        }}
                      />
                      <span
                        aria-hidden="true"
                        className={cn(
                          'flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px]',
                          activa ? 'border-primary' : 'border-border-strong'
                        )}
                      >
                        {activa && <span className="bg-primary size-2 rounded-full" />}
                      </span>
                      <Icon
                        name="mail"
                        size={17}
                        className={activa ? 'text-primary-hover' : 'text-icon-soft'}
                      />
                      <span
                        className={cn(
                          'fc-num fc-truncate min-w-0 flex-1 text-[13px]',
                          activa ? 'text-primary-strong' : 'text-body'
                        )}
                      >
                        {opcion.email_mask}
                      </span>
                    </label>
                  )
                })}
              </fieldset>
            )}

            <div className={cn(emailBloqueado && 'opacity-55')}>
              <CampoAcceso
                id="rec-email"
                label="Escribe ese correo completo"
                icono="mail"
                type="email"
                autoComplete="email"
                ref={emailRef}
                placeholder={emailBloqueado ? 'Elige una opción de arriba' : 'tu.correo@empresa.cl'}
                value={email}
                onChange={setEmail}
                onBlur={() => setEmailTocado(true)}
                disabled={isLoading || emailBloqueado}
                listo={emailOk}
                error={
                  emailTocado && desajustado ? 'No coincide con la pista que elegiste' : ''
                }
                nota={emailBloqueado ? 'Primero elige cuál de los correos es el tuyo.' : ''}
              />
            </div>

            <Button
              type="submit"
              size="lg"
              className={cn('w-full', claseBotonAcceso(emailOk))}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Icon name="progress_activity" size={18} className="animate-spin" />
                  Enviando…
                </>
              ) : (
                'Enviar enlace'
              )}
            </Button>
          </AccesoCuerpo>
        </form>
      )}

      {paso === 'enviado' && (
        <div className="flex flex-col items-center gap-4 px-[26px] pt-6 pb-[22px] text-center">
          <div className="bg-success-bg flex size-12 items-center justify-center rounded-full">
            <Icon name="mark_email_read" size={26} className="text-success" />
          </div>
          <div>
            <h1 className="mb-1.5 text-[19px] font-bold tracking-[-0.02em]">Revisa tu correo</h1>
            <p className="text-body text-[13px] leading-[1.55]">
              Si los datos son correctos, enviamos el enlace para crear una contraseña nueva a{' '}
              <strong className="font-semibold">{email}</strong>.
            </p>
          </div>

          <div className="bg-surface-muted border-border flex w-full flex-col gap-[9px] rounded-[10px] border p-[13px] text-left">
            {CONSEJOS.map((consejo) => (
              <div key={consejo.icono} className="flex items-start gap-[9px]">
                <Icon name={consejo.icono} size={16} className="text-soft mt-px" />
                <span className="text-body text-[11.5px] leading-[1.5]">{consejo.texto}</span>
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            className="h-[38px] w-full"
            onClick={() => {
              setPaso('email')
              setEmailTocado(false)
            }}
          >
            <Icon name="refresh" size={17} />
            No me llegó, revisar el correo
          </Button>
        </div>
      )}

      <AccesoPieTarjeta className="justify-center gap-4 py-[13px]">
        {paso === 'email' && (
          <button
            type="button"
            onClick={cambiarRut}
            className="text-body hover:text-foreground inline-flex cursor-pointer items-center gap-[5px] text-[12.5px]"
          >
            <Icon name="arrow_back" size={16} />
            Cambiar RUT
          </button>
        )}
        <Link
          href="/login"
          className="text-primary-hover hover:text-primary-strong text-[12.5px] font-medium"
        >
          Volver al inicio de sesión
        </Link>
      </AccesoPieTarjeta>
    </AccesoLayout>
  )
}
